<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import draggable from 'vuedraggable';
import KanbanCard from './KanbanCard.vue';

const props = defineProps({
  column: {
    type: Object,
    required: true
  },
  allKanbanLabels: {
    type: Array,
    required: true
  }
});

const emit = defineEmits(['edit-column']);

const store = useStore();

const columnItems = ref([]);

watch(() => props.column.items, (newItems) => {
  columnItems.value = [...newItems];
}, { immediate: true });

const columnBgStyle = computed(() => {
  const hex = props.column.label.color;
  if (!hex) return {};
  const r = parseInt(hex.slice(1, 3), 16) || 0;
  const g = parseInt(hex.slice(3, 5), 16) || 0;
  const b = parseInt(hex.slice(5, 7), 16) || 0;
  return { backgroundColor: `rgba(${r}, ${g}, ${b}, 0.12)` };
});

const columnHeaderStyle = computed(() => {
  const hex = props.column.label.color;
  if (!hex) return {};
  const r = parseInt(hex.slice(1, 3), 16) || 0;
  const g = parseInt(hex.slice(3, 5), 16) || 0;
  const b = parseInt(hex.slice(5, 7), 16) || 0;
  return { 
    backgroundColor: `rgba(${r}, ${g}, ${b}, 0.25)`,
    borderTop: `4px solid ${hex}` 
  };
});

const onDragChange = (evt) => {
  if (evt.added) {
    const chat = evt.added.element;
    const currentLabels = chat.labels || [];
    
    // Find kanban labels
    const kanbanTitles = props.allKanbanLabels.map(l => l.title);
    
    // Remove old kanban labels and add the new one
    const newLabels = currentLabels.filter(l => !kanbanTitles.includes(l));
    
    if (!props.column.isDefault) {
      newLabels.push(props.column.label.title);
    }
    
    // Optmistic Update locally to prevent UI flicker
    chat.labels = newLabels;
    chat.custom_attributes = { ...chat.custom_attributes, kanban_entered_at: Math.floor(Date.now() / 1000) };
    
    // Dispatch to Chatwoot API
    store.dispatch('conversationLabels/update', {
      conversationId: chat.id,
      labels: newLabels
    });
    
    store.dispatch('updateCustomAttributes', {
      conversationId: chat.id,
      customAttributes: { kanban_entered_at: Math.floor(Date.now() / 1000) }
    });
  }
};
</script>

<template>
  <div class="flex flex-col bg-n-slate-2 rounded-lg min-w-[320px] max-w-[320px] max-h-full border border-n-slate-4 shrink-0 shadow-sm">
    <div 
      class="column-drag-handle p-3 border-b border-n-slate-4 flex justify-between items-center bg-n-surface-1 rounded-t-lg shrink-0 group cursor-grab active:cursor-grabbing hover:brightness-95 transition-all"
      :style="columnHeaderStyle"
    >
      <div class="flex items-center gap-2">
        <span class="i-lucide-grip-vertical text-n-slate-9 size-4 opacity-50 group-hover:opacity-100 transition-opacity"></span>
        <span class="w-3 h-3 rounded-full" :style="{ backgroundColor: column.label.color }"></span>
        <h3 class="font-semibold text-n-slate-12 m-0 flex items-center gap-2">
          {{ column.label.title }}
          <button v-if="!column.isDefault" class="text-n-slate-9 hover:text-n-brand-5 transition-colors p-1 rounded hover:bg-n-slate-3" @click="$emit('edit-column', column.label)" title="Editar Coluna (Etiqueta)">
            <span class="i-lucide-pencil size-3.5"></span>
          </button>
        </h3>
      </div>
      <span class="bg-n-slate-3 text-n-slate-11 text-xs font-medium px-2 py-0.5 rounded-full" :title="column.apiMeta?.all_count ? `Mostrando ${column.items.length} de ${column.apiMeta.all_count}` : ''">
        {{ column.apiMeta?.all_count || column.items.length }}
      </span>
    </div>
    
    <draggable
      v-model="columnItems"
      group="kanban-conversations"
      @change="onDragChange"
      item-key="id"
      :delay="100"
      :delayOnTouchOnly="true"
      class="flex-1 overflow-y-auto p-3 flex flex-col gap-3 min-h-[150px] rounded-b-lg"
      :style="columnBgStyle"
      ghost-class="opacity-50"
    >
      <template #item="{element}">
        <KanbanCard :chat="element" :all-kanban-labels="allKanbanLabels" />
      </template>
    </draggable>
  </div>
</template>
